// Unified view-state engine (ADR-juuplan-tasks-clickup-overhaul D4).
//
// One typed model — view + groupBy + multi-sort + composable filters + hidden
// columns — and three pure, source-agnostic operations over TaskRecord[]:
//   applyFilters → applySort → bucket
// Every task surface (project page, "Meine Aufgaben" hub, personal Lists) drives
// the same engine via an opaque `scopeKey`; persistence lives in viewStateStore.ts.

import type { StatusCategory, TaskPriority } from "@juuplan/shared";
import { TASK_PRIORITY_LABEL, TASK_PRIORITY_VAR, localTodayIso } from "./format";
import type { TaskRecord } from "./taskRecord";

// ---------- model ----------

export type ViewKind = "list" | "board" | "calendar" | "gantt";

export type GroupBy =
  | "none"
  | "phase"
  | "status"
  | "priority"
  | "assignee"
  | "due"
  | "project"
  | "list"
  | "type";

export type SortKey =
  | "manual"
  | "title"
  | "due"
  | "priority"
  | "status"
  | "created"
  | "updated";

export interface SortRule {
  key: SortKey;
  dir: "asc" | "desc";
}

export type FilterField =
  | "text"
  | "status"
  | "priority"
  | "assignee"
  | "label"
  | "due"
  | "project"
  | "type"
  | "done";

export type FilterOp =
  | "is"
  | "is_not"
  | "any"
  | "none"
  | "before"
  | "after"
  | "overdue"
  | "today"
  | "this_week"
  | "contains";

export interface FilterRule {
  field: FilterField;
  op: FilterOp;
  /** number | string | number[] depending on field/op; ignored for relative ops. */
  value?: unknown;
}

export type RecordColumnId =
  | "status"
  | "assignees"
  | "labels"
  | "start"
  | "end"
  | "priority"
  | "project"
  | "ticket";

export interface ViewState {
  view: ViewKind;
  groupBy: GroupBy;
  sort: SortRule[];
  filters: FilterRule[];
  hiddenColumns: RecordColumnId[];
}

export const DEFAULT_VIEW_STATE: ViewState = {
  view: "list",
  groupBy: "none",
  sort: [{ key: "manual", dir: "asc" }],
  filters: [],
  hiddenColumns: [],
};

export const GROUP_BY_LABEL: Record<GroupBy, string> = {
  none: "Keine",
  phase: "Phase",
  status: "Status",
  priority: "Priorität",
  assignee: "Zuständig",
  due: "Fälligkeit",
  project: "Projekt",
  list: "Liste",
  type: "Typ",
};

// ---------- bucket shape ----------

export interface Bucket {
  key: string;
  label: string;
  color: string;
  items: TaskRecord[];
}

export interface BucketOptions {
  /**
   * Ordered project/list custom statuses — drives status grouping order + empty
   * buckets. `category` (when supplied) lets grouping route a record with a null
   * status_id into the list's default open/done status instead of "Sonstiges".
   */
  statuses?: { id: number; name: string; color: string; category?: StatusCategory }[];
  /** Ordered phases — drives phase grouping order + empty buckets. */
  phases?: { id: number; name: string; color: string | null }[];
  /** Ordered work-item types — drives type grouping order + empty buckets. */
  types?: { id: number; name: string; color: string }[];
  resolveUserName?: (id: number) => string;
  resolveProject?: (id: number) => { name: string; color: string | null } | undefined;
  resolveList?: (id: number) => { name: string; color: string | null } | undefined;
  /** ISO date (YYYY-MM-DD) treated as "today" for due bucketing — injectable for tests. */
  today?: string;
}

// ---------- ordering constants ----------

const PRIORITY_RANK: Record<TaskPriority | "none", number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4,
};

const PRIORITY_ORDER: (TaskPriority | "none")[] = ["urgent", "high", "medium", "low", "none"];

const CATEGORY_RANK: Record<string, number> = {
  backlog: 0,
  unstarted: 1,
  started: 2,
  review: 3,
  done: 4,
  cancelled: 5,
};

// ---------- date helpers ----------

/** "today" — uses the injected value or the real date. Returns YYYY-MM-DD. */
function todayIso(today?: string): string {
  if (today) return today;
  return localTodayIso();
}

/** Monday-based end of the current ISO week (YYYY-MM-DD), inclusive. */
function endOfWeekIso(today: string): string {
  const d = new Date(`${today}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7; // 0 = Monday
  d.setUTCDate(d.getUTCDate() + (6 - dow));
  return d.toISOString().slice(0, 10);
}

// ---------- filters ----------

function matchesFilter(r: TaskRecord, f: FilterRule, today: string): boolean {
  switch (f.field) {
    case "text": {
      const q = String(f.value ?? "").trim().toLowerCase();
      if (!q) return true;
      return r.title.toLowerCase().includes(q);
    }
    case "status": {
      const v = (f.value ?? null) as number | null;
      const eq = r.status.id === v;
      return f.op === "is_not" ? !eq : eq;
    }
    case "priority": {
      const v = (f.value ?? null) as TaskPriority | null;
      const eq = r.priority === v;
      return f.op === "is_not" ? !eq : eq;
    }
    case "assignee": {
      if (f.op === "none") return r.assigneeIds.length === 0;
      const vals = (Array.isArray(f.value) ? f.value : [f.value]) as number[];
      const has = vals.some((id) => r.assigneeIds.includes(id));
      return f.op === "is_not" ? !has : has;
    }
    case "label": {
      const vals = (Array.isArray(f.value) ? f.value : [f.value]) as number[];
      const has = vals.some((id) => r.labelIds.includes(id));
      return f.op === "none" ? r.labelIds.length === 0 : f.op === "is_not" ? !has : has;
    }
    case "project": {
      const v = (f.value ?? null) as number | null;
      const eq = r.projectId === v;
      return f.op === "is_not" ? !eq : eq;
    }
    case "type": {
      // typeId === null means "no work-item type assigned" (personal tasks,
      // or project tasks predating work-item types / not yet typed). It only
      // matches "is"/"is_not" when the filter value is explicitly null or
      // absent; an "any" list of concrete type ids never matches it — mirrors
      // how `assignee`/`label` require an explicit "none" op to catch the
      // unassigned case, which this ticket doesn't need.
      const typeId = r.typeId ?? null;
      if (f.op === "any") {
        const vals = (Array.isArray(f.value) ? f.value : [f.value]) as (number | null)[];
        return vals.includes(typeId);
      }
      const v = (f.value ?? null) as number | null;
      const eq = typeId === v;
      return f.op === "is_not" ? !eq : eq;
    }
    case "done": {
      // Pure view-only filter (#873 "hide completed"): GroupSortFilterBar's
      // toggle sets { op: "is", value: false } to keep only not-done records.
      // Design decision: filtering here, upstream of bucket(), means
      // bucketByStatus's existing "always emit every configured status
      // column, even empty" behavior (see comment there) is what keeps the
      // board's Done column visible-but-empty rather than making it
      // disappear — no extra special-casing needed, and the column layout
      // doesn't jump as the user flips the toggle.
      const want = f.value === undefined ? true : Boolean(f.value);
      const eq = r.done === want;
      return f.op === "is_not" ? !eq : eq;
    }
    case "due": {
      const due = r.dueDate;
      if (f.op === "overdue") return !!due && due < today && !r.done;
      if (f.op === "today") return due === today;
      if (f.op === "this_week") return !!due && due >= today && due <= endOfWeekIso(today);
      if (f.op === "before") return !!due && !!f.value && due < String(f.value);
      if (f.op === "after") return !!due && !!f.value && due > String(f.value);
      return true;
    }
    default:
      return true;
  }
}

/** Keep records matching ALL filter rules (AND semantics). */
/**
 * Does one record pass ALL the given rules? The single source of truth for
 * filter semantics — `applyFilters` below delegates to it, and the project
 * views (which render `Task`, not `TaskRecord`, and used to carry their own
 * hand-written predicates) call it through `projectTaskToRecord`.
 *
 * Unknown filter fields are a deliberate PASS-THROUGH (`matchesFilter`'s
 * `default: return true`). `routes/Project.tsx` relies on that: it smuggles its
 * active view tab and quick-filter into the same persisted `filters` array as
 * synthetic `_view` / `_filter` rules, and those must never exclude a record.
 */
export function matchesAllFilters(
  record: TaskRecord,
  filters: FilterRule[],
  opts: { today?: string } = {}
): boolean {
  if (filters.length === 0) return true;
  const today = todayIso(opts.today);
  return filters.every((f) => matchesFilter(record, f, today));
}

export function applyFilters(
  records: TaskRecord[],
  filters: FilterRule[],
  opts: { today?: string } = {}
): TaskRecord[] {
  if (filters.length === 0) return records;
  // Resolve "today" once for the whole pass, then delegate per record so the
  // predicate itself exists exactly once.
  const today = todayIso(opts.today);
  return records.filter((r) => matchesAllFilters(r, filters, { today }));
}

// ---------- sort ----------

function compareOne(a: TaskRecord, b: TaskRecord, rule: SortRule): number {
  let n = 0;
  switch (rule.key) {
    case "manual":
      n = a.sortOrder - b.sortOrder;
      break;
    case "title":
      n = a.title.localeCompare(b.title, "de", { sensitivity: "base" });
      break;
    case "due":
      // nulls always last regardless of direction
      if (!a.dueDate && !b.dueDate) n = 0;
      else if (!a.dueDate) return 1;
      else if (!b.dueDate) return -1;
      else n = a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0;
      break;
    case "priority":
      n = PRIORITY_RANK[a.priority ?? "none"] - PRIORITY_RANK[b.priority ?? "none"];
      break;
    case "status":
      n = (CATEGORY_RANK[a.status.category] ?? 99) - (CATEGORY_RANK[b.status.category] ?? 99);
      break;
    case "created":
      n = a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0;
      break;
    case "updated":
      n = a.updatedAt < b.updatedAt ? -1 : a.updatedAt > b.updatedAt ? 1 : 0;
      break;
  }
  return rule.dir === "desc" ? -n : n;
}

/** Stable multi-key sort. Returns a new array. */
export function applySort(records: TaskRecord[], rules: SortRule[]): TaskRecord[] {
  if (rules.length === 0) return records.slice();
  return records
    .map((r, i) => ({ r, i }))
    .sort((x, y) => {
      for (const rule of rules) {
        const n = compareOne(x.r, y.r, rule);
        if (n !== 0) return n;
      }
      return x.i - y.i; // stable tiebreak
    })
    .map((w) => w.r);
}

// ---------- bucket ----------

function bucketByStatus(records: TaskRecord[], opts: BucketOptions): Bucket[] {
  if (opts.statuses && opts.statuses.length > 0) {
    const byId = new Map<number, TaskRecord[]>();
    for (const s of opts.statuses) byId.set(s.id, []);
    // A listed task with a null status_id (legacy row, never assigned one) is
    // rendered with a synthesized "Offen"/"Erledigt" status carrying id === null
    // (see personalStatus in taskRecord.ts). Route it to the list's DEFAULT
    // status — first "unstarted" (or, when done, first done/cancelled) status by
    // order, mirroring the server's resolveDefaultListStatusId / resolveDoneListStatusId —
    // so it groups under "Offen" instead of the "Sonstiges" fallback. A non-null
    // but foreign id (e.g. a status from another list) still lands in Sonstiges.
    const openDefault =
      opts.statuses.find((s) => s.category === "unstarted")?.id ?? opts.statuses[0]?.id ?? null;
    const doneDefault =
      opts.statuses.find((s) => s.category === "done" || s.category === "cancelled")?.id ?? null;
    const fallback: TaskRecord[] = [];
    for (const r of records) {
      if (r.status.id !== null && byId.has(r.status.id)) {
        byId.get(r.status.id)!.push(r);
        continue;
      }
      if (r.status.id === null) {
        const target = r.done ? doneDefault : openDefault;
        if (target !== null && byId.has(target)) {
          byId.get(target)!.push(r);
          continue;
        }
      }
      fallback.push(r);
    }
    const out: Bucket[] = opts.statuses.map((s) => ({
      key: `status:${s.id}`,
      label: s.name,
      color: s.color,
      items: byId.get(s.id) ?? [],
    }));
    if (fallback.length > 0)
      out.push({ key: "status:_fallback", label: "Sonstiges", color: "var(--color-outline)", items: fallback });
    return out;
  }
  // Cross-source fallback: group by status category.
  const byCat = new Map<string, TaskRecord[]>();
  for (const r of records) {
    const c = r.status.category;
    if (!byCat.has(c)) byCat.set(c, []);
    byCat.get(c)!.push(r);
  }
  return Array.from(byCat.entries())
    .sort((a, b) => (CATEGORY_RANK[a[0]] ?? 99) - (CATEGORY_RANK[b[0]] ?? 99))
    .map(([cat, items]) => ({
      key: `cat:${cat}`,
      label: items[0]?.status.label ?? cat,
      color: items[0]?.status.color ?? "var(--color-outline)",
      items,
    }));
}

function bucketByPriority(records: TaskRecord[]): Bucket[] {
  const byPrio = new Map<TaskPriority | "none", TaskRecord[]>();
  for (const p of PRIORITY_ORDER) byPrio.set(p, []);
  for (const r of records) byPrio.get(r.priority ?? "none")!.push(r);
  const out: Bucket[] = [];
  for (const p of PRIORITY_ORDER) {
    const items = byPrio.get(p)!;
    if (items.length === 0) continue;
    out.push({
      key: `prio:${p}`,
      label: p === "none" ? "Ohne Priorität" : TASK_PRIORITY_LABEL[p],
      color: p === "none" ? "var(--color-outline)" : TASK_PRIORITY_VAR[p],
      items,
    });
  }
  return out;
}

function bucketByAssignee(records: TaskRecord[], opts: BucketOptions): Bucket[] {
  const unassigned: TaskRecord[] = [];
  const byUser = new Map<number, TaskRecord[]>();
  for (const r of records) {
    const uid = r.primaryAssigneeId;
    if (uid === null) unassigned.push(r);
    else {
      if (!byUser.has(uid)) byUser.set(uid, []);
      byUser.get(uid)!.push(r);
    }
  }
  const resolve = opts.resolveUserName ?? ((id: number) => `User #${id}`);
  const entries = Array.from(byUser.entries())
    .map(([id, items]) => ({ id, name: resolve(id), items }))
    .sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }));
  const out: Bucket[] = [];
  if (unassigned.length > 0)
    out.push({ key: "user:none", label: "Nicht zugewiesen", color: "var(--color-outline)", items: unassigned });
  for (const e of entries)
    out.push({ key: `user:${e.id}`, label: e.name, color: "var(--color-primary)", items: e.items });
  return out;
}

function bucketByPhase(records: TaskRecord[], opts: BucketOptions): Bucket[] {
  const byPhase = new Map<number, TaskRecord[]>();
  const orphans: TaskRecord[] = [];
  for (const r of records) {
    if (r.phaseId === null) orphans.push(r);
    else {
      if (!byPhase.has(r.phaseId)) byPhase.set(r.phaseId, []);
      byPhase.get(r.phaseId)!.push(r);
    }
  }
  const ordered = opts.phases ?? [];
  const out: Bucket[] = ordered.map((p) => ({
    key: `phase:${p.id}`,
    label: p.name,
    color: p.color ?? "var(--color-primary)",
    items: byPhase.get(p.id) ?? [],
  }));
  // Phases present on records but not in the ordered list (defensive).
  for (const [id, items] of byPhase) {
    if (!ordered.some((p) => p.id === id))
      out.push({ key: `phase:${id}`, label: `Phase #${id}`, color: "var(--color-primary)", items });
  }
  if (orphans.length > 0)
    out.push({ key: "phase:none", label: "Ohne Phase", color: "var(--color-outline)", items: orphans });
  return out;
}

function bucketByType(records: TaskRecord[], opts: BucketOptions): Bucket[] {
  const byType = new Map<number, TaskRecord[]>();
  const none: TaskRecord[] = [];
  for (const r of records) {
    const typeId = r.typeId ?? null;
    if (typeId === null) none.push(r);
    else {
      if (!byType.has(typeId)) byType.set(typeId, []);
      byType.get(typeId)!.push(r);
    }
  }
  const ordered = opts.types ?? [];
  const out: Bucket[] = ordered.map((t) => ({
    key: `type:${t.id}`,
    label: t.name,
    color: t.color,
    items: byType.get(t.id) ?? [],
  }));
  // Types present on records but not in the ordered list (defensive).
  for (const [id, items] of byType) {
    if (!ordered.some((t) => t.id === id))
      out.push({ key: `type:${id}`, label: `Typ #${id}`, color: "var(--color-primary)", items });
  }
  if (none.length > 0)
    out.push({ key: "type:none", label: "Ohne Typ", color: "var(--color-outline)", items: none });
  return out;
}

function bucketByDue(records: TaskRecord[], opts: BucketOptions): Bucket[] {
  const today = todayIso(opts.today);
  const eow = endOfWeekIso(today);
  const buckets: Record<string, { label: string; color: string; items: TaskRecord[] }> = {
    overdue: { label: "Überfällig", color: "var(--color-prio-urgent)", items: [] },
    today: { label: "Heute", color: "var(--color-prio-high)", items: [] },
    week: { label: "Diese Woche", color: "var(--color-prio-medium)", items: [] },
    later: { label: "Später", color: "var(--color-primary)", items: [] },
    none: { label: "Kein Datum", color: "var(--color-outline)", items: [] },
  };
  for (const r of records) {
    const d = r.dueDate;
    if (!d) buckets.none.items.push(r);
    else if (d < today) (r.done ? buckets.later : buckets.overdue).items.push(r);
    else if (d === today) buckets.today.items.push(r);
    else if (d <= eow) buckets.week.items.push(r);
    else buckets.later.items.push(r);
  }
  return (["overdue", "today", "week", "later", "none"] as const)
    .map((k) => ({ key: `due:${k}`, ...buckets[k] }))
    .filter((b) => b.items.length > 0);
}

function bucketByProject(records: TaskRecord[], opts: BucketOptions): Bucket[] {
  const byProject = new Map<number, TaskRecord[]>();
  const none: TaskRecord[] = [];
  for (const r of records) {
    if (r.projectId === null) none.push(r);
    else {
      if (!byProject.has(r.projectId)) byProject.set(r.projectId, []);
      byProject.get(r.projectId)!.push(r);
    }
  }
  const resolve = opts.resolveProject ?? (() => undefined);
  const entries = Array.from(byProject.entries())
    .map(([id, items]) => {
      const p = resolve(id);
      return { id, name: p?.name ?? `Projekt #${id}`, color: p?.color ?? "var(--color-primary)", items };
    })
    .sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }));
  const out: Bucket[] = entries.map((e) => ({
    key: `project:${e.id}`,
    label: e.name,
    color: e.color,
    items: e.items,
  }));
  if (none.length > 0)
    out.push({ key: "project:none", label: "Ohne Projekt", color: "var(--color-outline)", items: none });
  return out;
}

function bucketByList(records: TaskRecord[], opts: BucketOptions): Bucket[] {
  const byList = new Map<number, TaskRecord[]>();
  const none: TaskRecord[] = [];
  for (const r of records) {
    if (r.listId === null) none.push(r);
    else {
      if (!byList.has(r.listId)) byList.set(r.listId, []);
      byList.get(r.listId)!.push(r);
    }
  }
  const resolve = opts.resolveList ?? (() => undefined);
  const entries = Array.from(byList.entries())
    .map(([id, items]) => {
      const l = resolve(id);
      return {
        id,
        name: l?.name ?? `Liste #${id}`,
        color: l?.color ?? "var(--color-primary)",
        items,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }));
  const out: Bucket[] = entries.map((e) => ({
    key: `list:${e.id}`,
    label: e.name,
    color: e.color,
    items: e.items,
  }));
  // Rueckfallgruppe. Eine PERSOENLICHE Aufgabe ohne Liste gibt es nicht mehr
  // (Server-Migration 0092) — hier landen nur noch Projekt-Aufgaben, die per
  // Definition in keiner persoenlichen Liste liegen (projectTaskToRecord setzt
  // listId auf null). Die Gruppe wird nur gezeichnet, wenn sie etwas enthaelt;
  // in /lists (nur persoenliche Aufgaben) bleibt sie damit unsichtbar.
  if (none.length > 0)
    out.push({ key: "list:none", label: "Ohne Liste", color: "var(--color-outline)", items: none });
  return out;
}

/** Group records into ordered buckets according to `groupBy`. */
export function bucket(records: TaskRecord[], groupBy: GroupBy, opts: BucketOptions = {}): Bucket[] {
  switch (groupBy) {
    case "none":
      return [{ key: "all", label: "", color: "var(--color-outline)", items: records }];
    case "phase":
      return bucketByPhase(records, opts);
    case "status":
      return bucketByStatus(records, opts);
    case "priority":
      return bucketByPriority(records);
    case "assignee":
      return bucketByAssignee(records, opts);
    case "due":
      return bucketByDue(records, opts);
    case "project":
      return bucketByProject(records, opts);
    case "list":
      return bucketByList(records, opts);
    case "type":
      return bucketByType(records, opts);
    default:
      return [{ key: "all", label: "", color: "var(--color-outline)", items: records }];
  }
}

/** Convenience: filter → sort → bucket in one call. */
export function computeView(
  records: TaskRecord[],
  state: ViewState,
  opts: BucketOptions = {}
): Bucket[] {
  const filtered = applyFilters(records, state.filters, { today: opts.today });
  const sorted = applySort(filtered, state.sort);
  return bucket(sorted, state.groupBy, opts);
}
